import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

export type InboxItemStatus = 'pending' | 'accepted' | 'rejected';

export interface InboxItem {
  id: number;
  title: string;
  description: string | null;
  priority: string;
  tags: string | null;
  source_api_key_id: number;
  source_name: string;
  target_pseudo_project_id: number | null;
  target_project_id: number | null;
  target_project_name: string | null;
  status: InboxItemStatus;
  external_source: string | null;
  category: string | null;
  user_id: number | null;
  created_at: string;
  updated_at: string;
}

const PRIORITY_LABELS: Record<string, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  critical: 'Critical',
};

@customElement('cp-inbox-item-card')
export class CpInboxItemCard extends LitElement {
  createRenderRoot() { return this; }

  @property({ type: Object }) item!: InboxItem;
  @state() private _saving = false;
  @state() private _confirmOpen = false;

  private _formatDate(iso: string): string {
    try {
      const d = new Date(iso);
      return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
      return iso;
    }
  }

  private _truncateDescription(desc: string | null): string {
    if (!desc) return '';
    return desc.length > 120 ? desc.slice(0, 120) + '…' : desc;
  }

  private async _acceptItem(): Promise<void> {
    if (this._saving) return;
    this._saving = true;
    try {
      const res = await fetch(`/api/inbox/${this.item.id}/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`Accept failed: ${res.status}`);
      this.dispatchEvent(new CustomEvent('item-accepted', {
        detail: { itemId: this.item.id },
        bubbles: true,
        composed: true,
      }));
    } catch (err) {
      console.error('Failed to accept inbox item', err);
    } finally {
      this._saving = false;
    }
  }

  private _requestReject(): void {
    this._confirmOpen = true;
  }

  private async _rejectItem(): Promise<void> {
    this._confirmOpen = false;
    if (this._saving) return;
    this._saving = true;
    try {
      const res = await fetch(`/api/inbox/${this.item.id}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`Reject failed: ${res.status}`);
      this.dispatchEvent(new CustomEvent('item-rejected', {
        detail: { itemId: this.item.id },
        bubbles: true,
        composed: true,
      }));
    } catch (err) {
      console.error('Failed to reject inbox item', err);
    } finally {
      this._saving = false;
    }
  }

  private _cancelReject(): void {
    this._confirmOpen = false;
  }

  private _editItem(): void {
    this.dispatchEvent(new CustomEvent('item-edit-request', {
      detail: { item: this.item },
      bubbles: true,
      composed: true,
    }));
  }

  render() {
    const { item } = this;
    if (!item) return nothing;

    const tags = item.tags
      ? item.tags.split(',').map(t => t.trim()).filter(Boolean)
      : [];
    const isPending = item.status === 'pending';

    return html`
      <div class="cp-inbox-item-card cp-inbox-item-card--${item.status}">
        <div class="cp-inbox-item-card__header">
          <span class="cp-inbox-item-card__title">${item.title}</span>
          <span class="cp-inbox-item-card__priority cp-inbox-item-card__priority--${item.priority}">
            ${PRIORITY_LABELS[item.priority] ?? item.priority}
          </span>
        </div>

        ${item.description ? html`
          <div class="cp-inbox-item-card__body">
            <p class="cp-inbox-item-card__description">${this._truncateDescription(item.description)}</p>
          </div>
        ` : nothing}

        <div class="cp-inbox-item-card__meta">
          ${item.external_source === 'betterstack' ? html`
            <span class="cp-inbox-item-card__source-badge--betterstack">BTS</span>
          ` : nothing}
          <span class="cp-inbox-item-card__sender">Von: ${item.source_name}</span>
          ${item.target_project_name ? html`
            <span class="cp-inbox-item-card__project">Projekt: ${item.target_project_name}</span>
          ` : nothing}
          <span class="cp-inbox-item-card__timestamp">${this._formatDate(item.created_at)}</span>
        </div>

        ${tags.length > 0 ? html`
          <div class="cp-inbox-item-card__tags">
            ${tags.map(tag => html`<span class="cp-inbox-item-card__tag">${tag}</span>`)}
          </div>
        ` : nothing}

        ${isPending ? html`
          <div class="cp-inbox-item-card__actions">
            <button
              class="cp-inbox-item-card__btn cp-inbox-item-card__btn--accept"
              ?disabled=${this._saving}
              @click=${this._acceptItem}
            >${this._saving ? '…' : 'Annehmen'}</button>
            <button
              class="cp-inbox-item-card__btn cp-inbox-item-card__btn--edit"
              ?disabled=${this._saving}
              @click=${this._editItem}
            >Bearbeiten</button>
            <button
              class="cp-inbox-item-card__btn cp-inbox-item-card__btn--reject"
              ?disabled=${this._saving}
              @click=${this._requestReject}
            >Ablehnen</button>
          </div>
        ` : html`
          <div class="cp-inbox-item-card__status-badge cp-inbox-item-card__status-badge--${item.status}">
            ${item.status === 'accepted' ? 'Angenommen' : 'Abgelehnt'}
          </div>
        `}

        <cp-confirm-dialog
          .open=${this._confirmOpen}
          title="Item ablehnen"
          message="Möchtest du dieses Item wirklich ablehnen?"
          confirmLabel="Ablehnen"
          variant="danger"
          @dialog-confirmed=${this._rejectItem}
          @dialog-cancelled=${this._cancelReject}
        ></cp-confirm-dialog>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-inbox-item-card': CpInboxItemCard;
  }
}
